import React from 'react';
import { Drawer, List, ListItem, ListItemIcon, ListItemText, Box, Typography, Divider, useTheme, useMediaQuery } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import DashboardIcon from '@mui/icons-material/Dashboard';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import EventIcon from '@mui/icons-material/Event';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import SettingsIcon from '@mui/icons-material/Settings';

const DRAWER_WIDTH = 250;

interface MenuItem {
  label: string;
  path: string | ((gymId?: string) => string);
  icon: React.ReactNode;
}

interface SidebarProps {
  mobileOpen: boolean;
  onMobileClose: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ mobileOpen, onMobileClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { gyms, selectedGym } = useSelector((state: RootState) => state.gym);

  const getGymId = (): string | null => {
    if (selectedGym) return String(selectedGym.id);
    if (gyms.length > 0) return String(gyms[0].id);
    return null;
  };

  const menuItems: MenuItem[] = [
    { label: 'Dashboard', path: '/dashboard', icon: <DashboardIcon /> },
    { label: 'Gyms', path: '/gyms', icon: <FitnessCenterIcon /> },
    { label: 'Slots', path: '/slots', icon: <EventIcon /> },
    { label: 'Bookings', path: '/bookings', icon: <BookmarkIcon /> },
    { label: 'Analytics', path: (gymId) => gymId ? `/analytics/gyms/${gymId}` : '/gyms', icon: <AnalyticsIcon /> },
    { label: 'Settings', path: '/settings', icon: <SettingsIcon /> }
  ];

  const handleNavigation = (pathOrFunc: string | ((gymId?: string) => string)) => {
    const gymId = getGymId();
    const path = typeof pathOrFunc === 'function' ? pathOrFunc(gymId || undefined) : pathOrFunc;
    navigate(path);
    if (isMobile) {
      onMobileClose();
    }
  };

  const drawerContent = (
    <>
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#14B8A6' }}>
          HUSLRZ
        </Typography>
        <Typography variant="caption" sx={{ color: '#666' }}>
          Partner Portal
        </Typography>
      </Box>
      <Divider />
      <List>
        {menuItems.map((item, index) => {
          const isActive = typeof item.path === 'string' 
            ? location.pathname === item.path || location.pathname.startsWith(item.path)
            : location.pathname.includes('/analytics');
          
          return (
            <ListItem
              button
              key={`${item.label}-${index}`}
              onClick={() => handleNavigation(item.path)}
              sx={{
                backgroundColor: isActive ? '#E0F2F1' : 'transparent',
                borderLeft: isActive ? '4px solid #14B8A6' : 'none',
                '&:hover': {
                  backgroundColor: '#f0f0f0'
                }
              }}
            >
              <ListItemIcon sx={{ color: isActive ? '#14B8A6' : '#666' }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                sx={{
                  color: isActive ? '#14B8A6' : '#333',
                  fontWeight: isActive ? 'bold' : 'normal'
                }}
              />
            </ListItem>
          );
        })}
      </List>
    </>
  );

  return (
    <>
      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{
          keepMounted: true // Better mobile performance
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            backgroundColor: '#f5f5f5'
          }
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Desktop Drawer */}
      <Drawer
        sx={{
          display: { xs: 'none', md: 'block' },
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            backgroundColor: '#f5f5f5'
          }
        }}
        variant="permanent"
        anchor="left"
      >
        {drawerContent}
      </Drawer>
    </>
  );
};

export default Sidebar;
